import { format, isDefined, isUndefined } from '@aloft/toolkit-shared';

import { Typography } from '../../../summary/components/Typography.tsx';
import { SummarySection } from '../SummarySection.tsx';
import { SummaryTableHorizontal } from '../SummaryTableHorizontal.tsx';
import { TitleAndExportToggleLockup } from '../TitleAndExportToggleLockup.tsx';
import { BreakdownColumn, Tkv2PairedCostSummaryInterfaceV2 } from '../types.ts';
import { adjustment } from '../utils/formatters/adjustment.ts';
import { percent } from '../utils/formatters/percent.ts';
import { wholeDollar } from '../utils/formatters/wholeDollar.ts';
import { getTableData } from '../utils/getTableData.ts';

const tableDef: BreakdownColumn[] = [
  {
    header: 'Eff Age | % Depreciation',
    columns: [
      { type: 'feature', adjustmentType: 'condition', path: 'effectiveAge' },
      { type: 'feature', adjustmentType: 'condition', path: 'percentDepreciation' },
    ],
    combiner: ([effAge, percentDep]) => {
      return `${isDefined(effAge) ? effAge : '-'} | ${
        isDefined(percentDep) ? format.percent(percentDep, 0) : '-'
      }`;
    },
  },
  {
    header: 'Difference in Dep',
    type: 'feature',
    adjustmentType: 'condition',
    path: 'depreciationDiffPercent',
    valueFormatter: percent,
  },
  {
    header: 'x Dwelling Cost New',
    type: 'feature',
    adjustmentType: 'condition',
    path: 'costNew',
    valueFormatter: wholeDollar,
  },
  {
    header: '= Adjustment',
    type: 'adjustment',
    adjustmentType: 'condition',
    path: 'adjustment',
    valueFormatter: adjustment,
  },
];

export const AdjustmentBreakdownDepreciatedCostMainDwellingCondition = (
  props: Tkv2PairedCostSummaryInterfaceV2,
) => {
  const tableData = getTableData(tableDef, props, props.compSequence);
  const sortedPercents = Object.values(tableData)
    .map((row) => row[1])
    .filter(isDefined)
    .sort((a, b) => (a as number) - (b as number));

  const minString = format.percent(sortedPercents[0]);
  const maxString = format.percent(sortedPercents[sortedPercents.length - 1]);
  const rangeString = [minString, maxString].join(' to ');

  if (isUndefined(minString) && isUndefined(maxString)) {
    return null;
  }

  return (
    <SummarySection marginBottom="2rem">
      <TitleAndExportToggleLockup
        {...props}
        title="Condition"
        id="isDepreciatedCostMainDwellingConditionInExport"
        variant="h3"
      />
      <Typography variant="body1">
        {`The condition adjustment is the difference in the precent depreciation between the subject and each comparable. These differences ranged from ${rangeString}.`}
      </Typography>
      <SummaryTableHorizontal
        {...props}
        summary={props}
        compSequence={props.compSequence}
        columns={tableDef}
        tableData={tableData}
        subjectHeader="SUB"
        compHeaderPrefix="CP"
      />
    </SummarySection>
  );
};
